import React, { Fragment, useState, useEffect, memo } from 'react';
import { Field, reduxForm } from 'redux-form';
import { withTranslation } from 'react-i18next';
import {
  ButtonToolbar,
  Button,
  Label,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import FileCopy from '@material-ui/icons/FileCopyOutlined';
import DateRange from '@material-ui/icons/DateRangeOutlined';
import { format, addDays } from 'date-fns';
import { Tooltip } from '@material-ui/core';
import validate from './Validate';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';
import renderField from '../../../shared/components/form/RenderField';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';
import renderDropZoneField from '../../../shared/components/form/DropZone';
import MenuItemsMaintenance from './MenuItemsMaintenance';
import MenuStudentRelationMaintenance from './MenuStudentRelationMaintenance';
import DateSelection from '../../../shared/components/form/DateSelection';
import Modal from '../../../shared/components/Modal';
import { ACTIVE_TAB } from '../../../shared/utils/constants';

const MenuForm = (props) => {
  const {
    t,
    dto,
    repeatItemsSubmitHandler,
    backHandler,
    handleSubmit,
    updateRelatedStudents,
    initialize,
    menuItemsWrapper,
    unsavedItems,
    setUnsavedItems,
    selectedDate,
    changeSelectedDate,
    toggleSaveMealModal,
    editField,
    toggleEditField,
    updateMenuItems,
    setMeal,
    meal,
    selectedStudents,
    setSelectStudent,
    studentsToSelect,
    setUnSelectStudent,
  } = props;
  const [inDetail, setInDetail] = useState(false);
  const [activeTab, setActiveTab] = useState(ACTIVE_TAB.ONE);
  const [leaveChangesConfirmation, setLeaveChangesConfirmation] =
    useState(false);
  const [newDate, setNewDate] = useState();
  const [lastSelectedDate, setLastSelectedDate] = useState();

  const toggleModal = () => {
    setLeaveChangesConfirmation(!leaveChangesConfirmation);
  };

  const updateLastSelectedDate = (date) => {
    setLastSelectedDate(date);
  };

  const toggleTab = (index) => {
    setActiveTab(index);
  };

  const inDetailChange = (event) => {
    if (event.target) {
      setInDetail(event.target.checked);
    }
  };

  const updateSelectedDate = (date) => {
    if (unsavedItems) {
      setLeaveChangesConfirmation(true);
      setNewDate(date);
      changeSelectedDate(date);
    } else {
      changeSelectedDate(date);
      setNewDate(date);
    }
  };

  const leaveChangesHandler = () => {
    setUnsavedItems(false);
    changeSelectedDate(newDate);
    toggleModal();
  };

  const repeatMenuItems = (initialDate, finalDate) => {
    if (finalDate.getTime() <= initialDate.getTime()) {
      showNotificationAutoDestroy(
        'warning',
        t('warnings.title'),
        t('repeat-menu.warnings.invalid-period')
      );
    } else {
      repeatItemsSubmitHandler(
        selectedDate.getTime(),
        initialDate.getTime(),
        finalDate.getTime(),
        dto.id
      );
    }
  };
  useEffect(() => {
    if (dto.fileUrl) {
      dto.files = [
        { preview: dto.fileUrl, type: dto.fileType, fileId: dto.fileId },
      ];
    }
    initialize({ ...dto });
    setInDetail(dto.inDetail);
  }, [dto]);

  return (
    <Fragment>
      <form className="form" onSubmit={handleSubmit}>
        <div className="form__form-group">
          <span className="form__form-group-label">{t('general.name')}</span>
          <div className="form__form-group-field">
            <Field
              name="name"
              type="text"
              component={renderField}
              maxLength="50"
              placeholder={t('placeholder.menu.name')}
            />
          </div>
        </div>
        <div className="form__form-group--2_columns">
          <div className="form__form-group-field">
            <Label
              style={{ paddingRight: '5px' }}
              className="form__form-group-label"
            >
              {t('general.active')}
            </Label>
            <Field
              name="active"
              component={renderCheckBoxField}
              className="colored-click"
            />
            <Label
              style={{ paddingRight: '5px' }}
              className="form__form-group-label"
            >
              {t('general.detailed-fill')}
            </Label>
            <Field
              name="inDetail"
              component={renderCheckBoxField}
              className="colored-click"
              onChange={inDetailChange}
            />
          </div>
          {inDetail ? (
            <Fragment>
              <Label className="form__form-label-inline">
                {format(selectedDate, 'dd/MM/yyyy')}
              </Label>
              <DateSelection
                changeHandler={updateSelectedDate}
                months={1}
                icon={<DateRange />}
                rangeSelection={false}
              />
              {dto.id ? (
                <DateSelection
                  changeHandler={repeatMenuItems}
                  months={2}
                  icon={
                    <Tooltip title={t('general.repeat-menu')}>
                      <FileCopy />
                    </Tooltip>
                  }
                  initialSelectedDate={addDays(
                    new Date(lastSelectedDate),
                    1
                  ).getTime()}
                  minDate={addDays(new Date(lastSelectedDate), 1).getTime()}
                  rangeSelection
                />
              ) : null}
            </Fragment>
          ) : null}
        </div>
        <Col md={12} lg={12} xl={12}>
          <div className="tabs tabs--justify tabs--bordered-bottom">
            <div className="tabs__wrap">
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: activeTab === ACTIVE_TAB.ONE,
                    })}
                    onClick={() => {
                      toggleTab(ACTIVE_TAB.ONE);
                    }}
                  >
                    {t('general.menu')}
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: activeTab === ACTIVE_TAB.TWO,
                    })}
                    onClick={() => {
                      toggleTab(ACTIVE_TAB.TWO);
                    }}
                  >
                    {t('general.students')}
                  </NavLink>
                </NavItem>
              </Nav>
              <TabContent activeTab={activeTab}>
                <TabPane tabId={ACTIVE_TAB.ONE} className="form">
                  <Fragment>
                    {inDetail ? (
                      <Fragment>
                        <MenuItemsMaintenance
                          editField={editField}
                          toggleSaveMealModal={toggleSaveMealModal}
                          updateLastSelectedDate={updateLastSelectedDate}
                          menuItemsWrapper={menuItemsWrapper}
                          toggleEditField={toggleEditField}
                          updateMenuItems={updateMenuItems}
                          setMeal={setMeal}
                          meal={meal}
                        />
                      </Fragment>
                    ) : (
                      <div className="form__form-group">
                        <div className="form__form-group-field">
                          <Field name="files" component={renderDropZoneField} />
                        </div>
                      </div>
                    )}
                  </Fragment>
                </TabPane>
                <TabPane tabId={ACTIVE_TAB.TWO}>
                  <MenuStudentRelationMaintenance
                    setSelectStudent={setSelectStudent}
                    selectedStudents={selectedStudents}
                    studentsToSelect={studentsToSelect}
                    setUnSelectStudent={setUnSelectStudent}
                    updateRelatedStudents={updateRelatedStudents}
                    leftTitle={t('menus-student-relation.available-students')}
                    rightTitle={t('menus-student-relation.selected-students')}
                  />
                </TabPane>
              </TabContent>
            </div>
          </div>
        </Col>
        <ButtonToolbar className="form__button-toolbar">
          <Button color="primary" onClick={backHandler}>
            {t('general.back')}
          </Button>
          <Button color="success" type="button" onClick={handleSubmit}>
            {t('general.save')}
          </Button>
        </ButtonToolbar>
      </form>
      <Modal
        color="warning"
        title={t('notifications.attention')}
        btn="Warning"
        message={t('repeat-menu.leave-changes-confirmation')}
        toggle={toggleModal}
        modal={leaveChangesConfirmation}
        onConfirm={leaveChangesHandler}
      />
    </Fragment>
  );
};

MenuForm.propTypes = {
  t: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  updateRelatedStudents: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  backHandler: PropTypes.func.isRequired,
  repeatItemsSubmitHandler: PropTypes.func.isRequired,
  dto: PropTypes.shape(PropTypes.shape).isRequired,
  menuItemsWrapper: PropTypes.shape(PropTypes.shape).isRequired,
  unsavedItems: PropTypes.bool.isRequired,
  setUnsavedItems: PropTypes.func.isRequired,
  selectedDate: PropTypes.shape(PropTypes.shape).isRequired,
  changeSelectedDate: PropTypes.func.isRequired,
  toggleSaveMealModal: PropTypes.func.isRequired,
  editField: PropTypes.shape(PropTypes.shape).isRequired,
  toggleEditField: PropTypes.func.isRequired,
  updateMenuItems: PropTypes.func.isRequired,
  setMeal: PropTypes.func.isRequired,
  meal: PropTypes.shape(PropTypes.shape).isRequired,
  selectedStudents: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape))
    .isRequired,
  studentsToSelect: PropTypes.arrayOf(PropTypes.shape(PropTypes.shape))
    .isRequired,
  setSelectStudent: PropTypes.func.isRequired,
  setUnSelectStudent: PropTypes.func.isRequired,
};

export default reduxForm({
  form: 'MenuForm',
  validate,
  keepDirtyOnReinitialize: true,
  enableReinitialize: true,
})(withTranslation('common')(memo(MenuForm)));
